import {
	BadgeCheck,
	BriefcaseBusiness,
	Check,
	ChevronDown,
	FileBadge,
	GraduationCap,
	Lightbulb,
	ThumbsUp,
	TriangleAlert,
} from "lucide-react";
import React, { useState } from "react";

import { Button } from "@/app/components/ui/button";
import { cn } from "@/app/handlers/cn.utils";

import { Badge } from "../../../components/Badge/Badge";
import { FitCheckAssessment } from "../config/api";

interface FitCheckReportProps {
	fitCheck: FitCheckAssessment;
}

interface BaselineItemProps {
	title: string;
	status: "MEET" | "PARTIAL" | "MISSING";
	content: {
		strengths?: string[];
		gaps?: string[];
		recommendations?: string;
	};
	icon: React.ReactNode;
}

const BaselineItem = ({ title, status, content, icon }: BaselineItemProps) => {
	const [isOpen, setIsOpen] = useState(false);

	const getBgHex = (status: string | undefined) => {
		if (status === "MEET") return "#DCFCE7"; // tailwind green-200
		if (status === "PARTIAL" || status === "MISSING") return "#FEF9C2"; // tailwind yellow-200 (approx)
		return undefined;
	};

	const getSquareStyles = (status: string | undefined) => {
		if (status === "MEET") {
			return { bg: "#F0FDF4", textClass: "text-[#017336]" };
		}
		if (status === "PARTIAL" || status === "MISSING") {
			return { bg: "#FEFCE8", textClass: "text-[#985508]" };
		}
		return { bg: "#FFFFFF", textClass: "text-gray-700" };
	};

	const square = getSquareStyles(status);

	return (
		<div
			className={cn("rounded-lg border transition-all overflow-hidden", {
				"border-[#B9F8CF]": status === "MEET",
				"border-[#FFF085]": status === "PARTIAL" || status === "MISSING",
			})}
		>
			<div
				onClick={() => setIsOpen(!isOpen)}
				className={cn("w-full py-4 px-2 flex items-center justify-between text-left cursor-pointer")}
				style={{ backgroundColor: getBgHex(status) }}
			>
				<div className="flex items-center gap-3 h-full">
					<div
						className={cn("w-12 aspect-square flex items-center justify-center rounded", square.textClass)}
						style={{ backgroundColor: square.bg }}
					>
						{icon}
					</div>
					<div>
						<p className="font-semibold text-sm text-gray-900">{title}</p>
						<Badge
							variant={status === "MEET" ? "default" : "secondary"}
							className={
								status === "MEET"
									? "bg-[#DFF7EA] text-[#017336] hover:bg-[#DFF7EA]"
									: "bg-[#FFECC7] text-[#985508] hover:bg-[#FFECC7]"
							}
						>
							{status === "MEET" ? "Match" : "Gap"}
						</Badge>
					</div>
				</div>
				<ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
			</div>
			{isOpen && (
				<div
					className="p-4"
					style={{
						backgroundColor: status === "MEET" ? "#F0FDF4" : "#FEFCE8",
					}}
				>
					<div className="grid gap-3 ">
						{/* Strengths */}
						{content.strengths.length > 0 && (
							<div className="rounded-md border border-[#DFF7EA] bg-white p-3">
								<div>
									<div className="flex items-center gap-2">
										<div className="text-[#017336] flex items-center justify-center">
											<ThumbsUp className="h-3 w-3" />
										</div>
										<p className="font-semibold text-sm text-gray-900">Strengths</p>
									</div>
									<div>
										<ul className="mt-2 space-y-1 text-sm text-gray-700">
											{content.strengths.map((s, i) => (
												<li key={i} className="flex items-start gap-2">
													<BadgeCheck className="h-3 w-3 text-green-600 flex-shrink-0 mt-1" />
													<span>{s}</span>
												</li>
											))}
										</ul>
									</div>
								</div>
							</div>
						)}

						{/* Weaknesses / Gaps */}
						{content.gaps.length > 0 && (
							<div className="rounded-md border border-[#FFF085] bg-white p-3">
								<div>
									<div className="flex items-center gap-2">
										<div className="text-[#985508] flex items-center justify-center">
											<TriangleAlert className="h-3 w-3" />
										</div>
										<p className="font-semibold text-sm text-gray-900">Gaps</p>
									</div>
									<div>
										<ul className="mt-2 space-y-1 text-sm text-gray-700">
											{content.gaps.map((g, i) => (
												<li key={i} className="flex items-start gap-2">
													<Check className="h-3 w-3 text-yellow-600 flex-shrink-0 mt-1" />
													<span>{g}</span>
												</li>
											))}
										</ul>
									</div>
								</div>
							</div>
						)}

						{/* Recommendations */}
						{content.recommendations && (
							<div className="rounded-md border border-gray-200 bg-white p-3">
								<div>
									<div className="flex items-center gap-2">
										<div className="text-[#3730A3] flex items-center justify-center">
											<Lightbulb className="w-4 h-4" />
										</div>
										<p className="font-semibold text-sm text-gray-900">Recommendations</p>
									</div>
									<div>
										<p className="mt-2 text-sm text-gray-700">{content.recommendations}</p>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

function FitCheckReport({ fitCheck }: FitCheckReportProps) {
	const baselineItems = [
		{ item: "Education", status: fitCheck.baselineCheck.education.status },
		{ item: "Experience", status: fitCheck.baselineCheck.experience.status },
		{ item: "Skills", status: fitCheck.baselineCheck.skills.status },
		{ item: "Certification", status: fitCheck.baselineCheck.certification.status },
	];

	return (
		<div className="space-y-8">
			{/* Baseline Check Section */}
			<section>
				<p className="text-lg font-semibold text-gray-900 mb-4">Baseline Check</p>

				{/* Status Badges */}
				<div className="flex flex-wrap gap-2 mb-4">
					{baselineItems.map(({ item, status }) => (
						<Badge
							key={item}
							variant={status === "MEET" ? "default" : "secondary"}
							className={
								status === "MEET"
									? "bg-green-100 text-green-700 hover:bg-green-100"
									: "bg-yellow-100 text-yellow-700 hover:bg-yellow-100"
							}
						>
							{status === "MEET" ? `Meets ${item}` : `Misses ${item}`}
						</Badge>
					))}
				</div>

				{/* Baseline Items */}
				<div className="space-y-3">
					<BaselineItem
						title="Education"
						status={fitCheck.baselineCheck.education.status}
						content={fitCheck.baselineCheck.education}
						icon={<GraduationCap className="w-6 h-6" />}
					/>
					<BaselineItem
						title="Work Experience"
						status={fitCheck.baselineCheck.experience.status}
						content={fitCheck.baselineCheck.experience}
						icon={<BriefcaseBusiness className="w-6 h-6" />}
					/>
					<BaselineItem
						title="Skills"
						status={fitCheck.baselineCheck.skills.status}
						content={fitCheck.baselineCheck.skills}
						icon={<Lightbulb className="w-6 h-6" />}
					/>
					<BaselineItem
						title="Certifications & Licenses"
						status={fitCheck.baselineCheck.certification.status}
						content={fitCheck.baselineCheck.certification}
						icon={<FileBadge className="w-6 h-6" />}
					/>
				</div>
			</section>

			<div>
				<div className="flex items-center gap-2 mb-2">
					<ThumbsUp className="w-4 h-4 text-green-700 flex-shrink-0 mt-0.5" />
					<div>
						<p className="text-md font-semibold text-gray-900">What You&apos;re Already Bringing</p>
					</div>
				</div>
				<ul className="space-y-2">
					{fitCheck.strengths.map((strength, index) => (
						<li key={index} className="flex items-start gap-2 text-gray-700">
							<Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
							<span>{strength}</span>
						</li>
					))}
				</ul>
			</div>

			<div>
				<div className="flex items-center gap-2 mb-2">
					<TriangleAlert className="w-4 h-4 text-yellow-700 flex-shrink-0 mt-0.5" />
					<div>
						<p className="text-md font-semibold text-gray-900">Where This Job Will Challenge You</p>
					</div>
				</div>
				<ul className="space-y-2">
					{fitCheck.gaps.map((gap, index) => (
						<li key={index} className="flex items-start gap-2 text-gray-700">
							<Check className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-1" />
							<span>{gap}</span>
						</li>
					))}
				</ul>
			</div>

			<div className="border border-gray-200 rounded">
				<div className="bg-gray-50 p-4">
					<p className="text-xl font-semibold text-gray-900">Decision Guidance</p>
					<p className="text-gray-700">{fitCheck.encouragementNote}</p>
				</div>

				<div className="p-4 flex flex-col gap-4">
					{/* Option 1: Apply Now */}
					<div className="bg-white rounded-lg p-6 border border-gray-200">
						<p className="text-md font-semibold text-gray-900">Option 1: Apply Now</p>
						<p className="text-sm text-gray-600 mb-3">
							You meet many requirements. Here&apos;s how to strengthen your application:
						</p>
						<ul className="space-y-3 mb-4">
							{fitCheck.decisionGuidance.ifApplyNow.map((tip, index) => (
								<li key={index} className="flex items-start gap-2 leading-tight text-[#3E424E]">
									<Check className="w-3 h-3 text-[#3E424E] flex-shrink-0 mt-1" />
									<span>{tip}</span>
								</li>
							))}
						</ul>
						<Button className="w-full" variant="tonal">
							Apply Now
						</Button>
					</div>

					{/* Option 2: Close Gaps First */}
					<div className="bg-white rounded-lg p-6 border border-gray-200">
						<p className="text-md font-semibold text-gray-900">Option 2: Close Gaps First</p>
						<p className="text-sm text-gray-600 mb-4">Prefer to improve first? Here&apos;s what to focus on:</p>
						<ul className="space-y-3 mb-4">
							{fitCheck.decisionGuidance.ifCloseGapsFirst.map((action, index) => (
								<li key={index} className="flex items-start gap-2 leading-tight text-[#3E424E]">
									<Check className="w-3 h-3 text-[#3E424E] flex-shrink-0 mt-1" />
									<span>{action}</span>
								</li>
							))}
						</ul>
						<Button variant="outline" className="w-full border-primary-700 text-primary-700">
							Close Gaps
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}

export default FitCheckReport;
